import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getAgentBalances,
  listAgentTransactions,
  listRuns,
  type Agent,
  type AgentRun,
  type AgentTx,
} from './api';

const TEMPLATE_LABEL: Record<string, string> = {
  payment: 'Payment',
  trading: 'Trading',
  compliance: 'Compliance',
  generic: 'Generic',
};

const STATUS_TONE: Record<string, string> = {
  active: 'bg-ok/10 text-ok border-ok/30',
  paused: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-surface-soft text-ink-tertiary border-line',
};

const PULSE_CELL_COUNT = 14;

export function AgentCard({ orgId, agent }: { orgId: string; agent: Agent }) {
  const runs = useQuery({
    queryKey: ['agent-card-runs', orgId, agent.id],
    queryFn: () => listRuns(orgId, agent.id),
    enabled: !!orgId,
    refetchInterval: 5000,
  });
  const txs = useQuery({
    queryKey: ['agent-card-txs', orgId, agent.id],
    queryFn: () => listAgentTransactions(orgId, agent.id, 1),
    enabled: !!orgId,
    refetchInterval: 10000,
  });

  const lastRun = runs.data?.[0];
  const lastTx = txs.data?.[0];
  const runsActive = runs.data?.some((r) => r.status === 'running' || r.status === 'pending') ?? false;

  return (
    <Link
      to={`/agents/${agent.id}`}
      className="group relative flex flex-col rounded-xl border border-line-soft bg-surface-white p-5 transition-all hover:border-bit2me hover:shadow-sm"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
            {TEMPLATE_LABEL[agent.template_slug] ?? agent.template_slug}
          </p>
          <h3 className="mt-1 truncate text-base font-semibold tracking-tightish text-ink-primary group-hover:text-bit2me">
            {agent.name}
          </h3>
        </div>
        <StatusBadge status={agent.status} active={runsActive} />
      </header>

      <WalletLine orgId={orgId} agent={agent} />

      <div className="mt-4">
        <RunPulse runs={runs.data ?? []} />
      </div>

      <div className="mt-4 min-h-[42px]">
        <LastActivityLine run={lastRun} tx={lastTx} />
      </div>

      <footer className="mt-4 grid grid-cols-2 gap-3 border-t border-line-soft pt-3">
        <Kpi label="Runs (7d)" value={String(runsLast7d(runs.data))} />
        <Kpi label="Tx (7d)" value={String(txsLast7d(txs.data))} />
      </footer>
    </Link>
  );
}

function WalletLine({ orgId, agent }: { orgId: string; agent: Agent }) {
  const enabled = !!agent.wallet_id;
  const balances = useQuery({
    queryKey: ['balances', orgId, agent.id],
    queryFn: () => getAgentBalances(orgId, agent.id),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!enabled) {
    return (
      <p className="mt-3 truncate font-mono text-[11px] text-ink-tertiary">no wallet</p>
    );
  }
  const data = balances.data;
  if (!data || !data.has_wallet) {
    return (
      <p className="mt-3 truncate font-mono text-[11px] text-ink-tertiary">
        {balances.isLoading ? 'loading…' : 'wallet unavailable'}
      </p>
    );
  }
  const primaryChain = data.default_chain ?? '';
  const primary =
    data.chains?.find((c) => c.chain === primaryChain) ??
    data.chains?.find((c) => (c.native?.raw ?? '0') !== '0' || (c.tokens?.length ?? 0) > 0) ??
    data.chains?.[0];
  const tokensWithBalance = primary?.tokens?.length ?? 0;

  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="font-mono text-[11px] text-ink-tertiary">
        {shortenAddress(data.address ?? '')}
      </span>
      {primary?.native && (
        <span className="font-mono text-[11px] text-ink-primary">
          {compactAmount(primary.native.human)} {primary.native.symbol}
        </span>
      )}
      {primary && (
        <span className="font-mono text-[10px] uppercase text-ink-tertiary">
          {primary.chain}
        </span>
      )}
      {tokensWithBalance > 0 && (
        <span className="font-mono text-[10px] text-ink-tertiary">
          +{tokensWithBalance} token{tokensWithBalance === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

function compactAmount(human: string): string {
  if (!human) return '0';
  const n = Number(human);
  if (Number.isNaN(n)) return human;
  if (Math.abs(n) >= 1) return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  if (n === 0) return '0';
  // Small numbers: keep up to 6 sig figs.
  return n.toPrecision(4);
}

function StatusBadge({ status, active }: { status: string; active: boolean }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.archived;
  return (
    <div className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${tone}`}>
      {active && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      <span>{active ? 'running' : status}</span>
    </div>
  );
}

function RunPulse({ runs }: { runs: AgentRun[] }) {
  // Newest on the right, oldest on the left.
  const recent = runs.slice(0, PULSE_CELL_COUNT).reverse();
  const cells = Array.from({ length: PULSE_CELL_COUNT }, (_, i) => {
    const run = recent[i + (recent.length - PULSE_CELL_COUNT < 0 ? 0 : 0)];
    return runCellTone(run);
  });
  return (
    <div className="flex items-center gap-[3px]">
      {cells.map((tone, i) => (
        <div key={i} className={`h-5 flex-1 rounded-sm ${tone}`} />
      ))}
    </div>
  );
}

function runCellTone(run: AgentRun | undefined): string {
  if (!run) return 'bg-surface-soft/60';
  if (run.status === 'pending') return 'bg-surface-soft border border-line';
  if (run.status === 'running') return 'bg-amber-300';
  if (run.status === 'failed') return 'bg-danger/80';
  if (run.outcome === 'completed') return 'bg-ok/80';
  if (run.outcome === 'failed' || run.outcome === 'timeout') return 'bg-danger/60';
  return 'bg-bit2me/60';
}

function LastActivityLine({ run, tx }: { run?: AgentRun; tx?: AgentTx }) {
  if (!run && !tx) {
    return <p className="text-xs text-ink-tertiary">No activity yet.</p>;
  }
  if (tx) {
    const amount = tx.token_symbol
      ? `${humanFromBaseUnits(tx.token_amount, 6)} ${tx.token_symbol}`
      : `${humanFromBaseUnits(tx.value_wei, 18)} native`;
    const target = shortenAddress(tx.recipient);
    return (
      <div className="space-y-1">
        <p className="truncate text-xs text-ink-primary">
          {amount} → {target}
        </p>
        <p className="font-mono text-[10px] text-ink-tertiary">
          {tx.status} · {tx.chain} · {timeAgo(tx.broadcast_at)}
        </p>
      </div>
    );
  }
  if (run) {
    return (
      <div className="space-y-1">
        <p className="truncate text-xs text-ink-primary">
          {run.summary ? truncate(run.summary, 64) : truncate(run.objective, 64)}
        </p>
        <p className="font-mono text-[10px] text-ink-tertiary">
          {run.outcome || run.status} · {timeAgo(run.created_at)}
        </p>
      </div>
    );
  }
  return null;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-ink-primary">{value}</p>
    </div>
  );
}

function runsLast7d(runs?: AgentRun[]): number {
  if (!runs) return 0;
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return runs.filter((r) => new Date(r.created_at).getTime() > cutoff).length;
}

function txsLast7d(txs?: AgentTx[]): number {
  if (!txs) return 0;
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return txs.filter((t) => new Date(t.broadcast_at).getTime() > cutoff).length;
}

function shortenAddress(a: string): string {
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function humanFromBaseUnits(raw: string | undefined, decimals: number): string {
  if (!raw) return '0';
  try {
    const n = BigInt(raw);
    if (n === 0n) return '0';
    if (decimals === 0) return n.toString();
    const div = 10n ** BigInt(decimals);
    const whole = n / div;
    const frac = (n % div).toString().padStart(decimals, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return raw;
  }
}
